import { Injectable } from '@angular/core';
import { TileValidationService } from '../../../../core/services/tile-validation.service';
import {
  CSVToggleTileRow,
  CSVNumericTileRow,
  CSVTileRow
} from '../models/csv-tile';
import {
  Tile,
  TileFormat,
  TileType,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_DEFAULT_WRITE_PRIORITY,
  TileId
} from '../../../../shared/models/tile';
import {
  SimpleBoolStructure,
  SimpleAnalogStructure,
  Condition,
  ConditionMode
} from '../../../../shared/models/structure';
import { Appearance, ColorOption } from '../../../../shared/models/appearance';
import { TileSetPointDecimal, TileSetPointMultiplier, TileSetPointConversion, ConversionFormula } from '../../../../shared/models/set-point';
import { TileIcon } from '../../../../shared/models/tile-icon';
import { DeviceId, DeviceType } from '../../../../shared/models/device';
import { AppId } from '../../../../shared/models/app';
import { CSVFieldParser } from '../utils/csv-field-parser.util';
import { LoRaWANPointFormat, LoRaWANPointType, makeLoRaWANPointID } from '../../../../shared/models/lorawan';
import { BULK_CREATION_PROTOCOL_HANDLERS } from '../../creation/utils/bulk-creation-protocol-handlers';
import { CsvFieldId } from '../constants/csv-columns.constants';
import { TAG_PROFILES } from '../../../../shared/constants/tag-profiles.constants';
import {
  AnalogAlarmOperator,
  CompareOperator,
} from '../../../../shared/constants/tile-config.constants';
import {
  TILE_APPEARANCE_DEFAULT_DECIMAL,
  TILE_APPEARANCE_DEFAULT_INCREMENT,
  TILE_APPEARANCE_DEFAULT_MULTIPLIER
} from '../../../../shared/models/appearance';
import { ModbusDataType } from '../../../../shared/models/modbus';
import {
  isBacnetToggleType,
  isBacnetNumericType,
  isModbusNumericType,
  ALARM_LEVEL_NAMES
} from '../constants/csv-validation.constants';
import { FormulaConfig, FormulaBinding, FormulaBindingSource, FormulaType, FORMULA_MAX_BINDINGS } from '../../../../shared/models/formula-config';
import { PROTOCOL_FORMULA } from '../../../../shared/models/protocol';
import { FORMULA_TEMPLATES } from '@features/formula-tile/shared/constants/formula-templates.constants';
import { extractFormulaVariables } from '@features/formula-tile/shared/utils/formula-parser.util';

@Injectable({
  providedIn: 'root'
})
export class CsvTileImporterService {
  constructor(private tileValidator: TileValidationService) {}

  parseTagLabels(value?: string): string[] {
    if (!value?.trim()) return [];
    return value.split('|').map(s => s.trim()).filter(Boolean);
  }

  parseFacetPairs(value?: string): Array<{key: string; value: string}> {
    if (!value?.trim()) return [];
    return value.split('|').map(part => {
      const eqIdx = part.indexOf('=');
      return eqIdx === -1
        ? {key: part.trim(), value: ''}
        : {key: part.slice(0, eqIdx).trim(), value: part.slice(eqIdx + 1).trim()};
    });
  }

  resolveProfileKeyFromCsv(csvData: any): string | null {
    const label = ((csvData as any)[CsvFieldId.PROFILE] ?? '').trim();
    if (!label) return null;
    return Object.keys(TAG_PROFILES).find(
      k => TAG_PROFILES[k].Label.toLowerCase() === label.toLowerCase()
    ) ?? null;
  }

  /**
   * Detect tile type from CSV row structure
   * Supports BACnet, Modbus, and LoRaWAN protocols
   * Also supports configuration CSVs by checking for type-specific columns
   */
  detectTileType(csvRow: CSVTileRow): TileFormat {
    // BACnet detection via BACnet Object Type
    const bacnetObjectType = csvRow[CsvFieldId.BACNET_OBJECT_TYPE];
    if (bacnetObjectType) {
      if (isBacnetToggleType(bacnetObjectType)) return TileFormat.Toggle;
      if (isBacnetNumericType(bacnetObjectType)) return TileFormat.Numeric;
    }

    // Modbus detection via Data Type
    const dataType = csvRow[CsvFieldId.MODBUS_DATA_TYPE];
    if (dataType) {
      if (dataType === ModbusDataType.Bool) return TileFormat.Toggle;
      if (isModbusNumericType(dataType)) return TileFormat.Numeric;
    }

    // LoRaWAN detection via Point Format
    const pointFormat = csvRow[CsvFieldId.LORAWAN_DATA_TYPE];
    if (pointFormat) {
      if (pointFormat === LoRaWANPointFormat.Boolean) return TileFormat.Toggle;
      if (pointFormat === LoRaWANPointFormat.Numeric) return TileFormat.Numeric;
    }

    // Fallback: Check for configuration CSV columns (for bulk configuration updates)
    // Toggle tiles have True/False phrase columns
    if (csvRow[CsvFieldId.DATA_DISPLAY_TRUE_PHRASE] !== undefined || csvRow[CsvFieldId.DATA_DISPLAY_FALSE_PHRASE] !== undefined) {
      return TileFormat.Toggle;
    }

    // Numeric tiles have Decimal Places, Units, Multiplier, etc.
    if (csvRow[CsvFieldId.DECIMAL_PLACES] !== undefined || csvRow[CsvFieldId.DATA_DISPLAY_UNITS] !== undefined) {
      return TileFormat.Numeric;
    }

    return TileFormat.Empty;
  }

  /**
   * Detect protocol from CSV row data
   * Returns DeviceType enum value for protocol
   */
  detectProtocol(csvRow: CSVTileRow): DeviceType | null {
    // BACnet is identified by the presence of 'BACnet Object Type' column
    if (csvRow[CsvFieldId.BACNET_INSTANCE_NUMBER] && csvRow[CsvFieldId.BACNET_OBJECT_TYPE]) {
      return DeviceType.BACnet;
    }

    // Modbus is identified by presence of 'Modbus Data Type' and 'Modbus Byte Order' columns
    if (csvRow[CsvFieldId.MODBUS_DATA_TYPE] && csvRow[CsvFieldId.MODBUS_BYTE_ORDER]) {
      return DeviceType.Modbus;
    }

    if (csvRow[CsvFieldId.LORAWAN_POINT_NAME] || csvRow[CsvFieldId.LORAWAN_DATA_SOURCE] || csvRow[CsvFieldId.LORAWAN_DATA_TYPE]) {
      return DeviceType.LoRaWAN;
    }

    return null;
  }

  /**
   * Build tile from CSV then validate the tile object
   * Keeps mapping and validation separate but does both in one call (no duplication)
   */
  buildAndValidateTile(row: CSVTileRow, deviceId?: DeviceId): { tile: Tile | null, formulaConfig?: FormulaConfig, errors: string[] } {
    const tagErrors = this.validateTagsAndFacets(row);
    if (tagErrors.length > 0) {
      return {tile: null, errors: tagErrors};
    }

    if ((row as any)[CsvFieldId.FORMULA_TYPE]) {
      return this.buildAndValidateFormulaTile(row);
    }

    const errors: string[] = [];

    // Check device ID - prioritize CSV Motion Device ID over context deviceId
    const csvDeviceId = row[CsvFieldId.MOTION_DEVICE_ID] as DeviceId;
    if (!csvDeviceId && !deviceId) {
      errors.push('Motion Device ID is required (provide via CSV or device selection)');
      return { tile: null, errors };
    }

    const effectiveDeviceId = csvDeviceId || deviceId;

    // Step 1: Detect protocol
    const protocol = this.detectProtocol(row);
    if (!protocol) {
      errors.push('Cannot detect protocol. Fill in the BACnet (Instance Number, Object Type), Modbus (Data Type, Byte Order) or LoRaWAN (Point Name, Data Source, Data Type) columns');
      return { tile: null, errors };
    }

    // Step 2: Parse and validate protocol fields, get constructed Point ID
    const { pointId, errors: protocolErrors } =
      BULK_CREATION_PROTOCOL_HANDLERS[protocol].parseAndValidatePointConfig(row);

    if (protocolErrors.length > 0) {
      return { tile: null, errors: protocolErrors };
    }

    const rawAutoClear = row[CsvFieldId.AUTO_CLEAR_ALARMS];
    if (rawAutoClear !== undefined && rawAutoClear !== '') {
      const autoClearResult = this.tileValidator.validateAutoClear(rawAutoClear);
      if (autoClearResult.errors.length > 0) {
        return { tile: null, errors: autoClearResult.errors };
      }
    }

    // Step 3: Map CSV to tile using constructed Point ID
    let tile: Tile | null;
    try {
      tile = this.mapTile(row, effectiveDeviceId, pointId!);
      if (!tile) {
        errors.push('Unknown or invalid tile type');
        return { tile: null, errors };
      }
    } catch (error: any) {
      errors.push(`Mapping error: ${error.message}`);
      return { tile: null, errors };
    }

    // Step 4: Validate the tile object
    const validationErrors = this.validateTile(tile);
    if (validationErrors.length > 0) {
      return { tile: null, errors: validationErrors };
    }

    return { tile, errors: [] };
  }

  /**
   * Validate a tile object (after mapping)
   * Note: Protocol-specific fields (BACnet Instance Number, BACnet Object Type, etc.) are validated before mapping
   */
  private validateTile(tile: Tile): string[] {
    const errors: string[] = [];

    // Common validations
    if (!tile.Name) {
      errors.push('Tile Name is required');
    }

    // Type-specific validations
    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      errors.push(...this.validateAnalogTile(tile));
    } else if (tile.Type === TILE_TYPE_SIMPLE_BOOL) {
      errors.push(...this.validateBoolTile(tile));
    }

    return errors;
  }

  /**
   * Validate analog (numeric) tile specific fields
   */
  private validateAnalogTile(tile: Tile): string[] {
    const errors: string[] = [];
    const structure = tile.Structure as SimpleAnalogStructure;

    // Validate alarm conditions
    structure.Conditions.forEach((condition, index) => {
      const alarmLevel = ALARM_LEVEL_NAMES[index];

      // Validate delay for all conditions (enabled or not)
      const delayResult = this.tileValidator.validateAlarmDelay(condition.Delay);
      errors.push(...delayResult.errors);

      if (condition.Enabled) {
        // Validate operator direction matches alarm level (high vs low)
        const operatorResult = this.tileValidator.validateAlarmOperator(
          alarmLevel,
          condition.Comparison.Operator
        );
        errors.push(...operatorResult.errors);

        // Validate alarm value is present and valid
        const valueResult = this.tileValidator.validateAlarmValue(
          alarmLevel,
          condition.Comparison.Value,
          condition.Enabled
        );
        errors.push(...valueResult.errors);
      }
    });

    // Validate threshold ordering (High Critical > High Warning > Low Warning > Low Critical)
    const thresholdResult = this.tileValidator.validateThresholdValues(structure.Conditions);
    errors.push(...thresholdResult.errors);

    // Validate appearance config (analog-specific)
    if (tile.Appearance?.Config) {
      const config = tile.Appearance.Config;

      // Decimal places
      if (config.Decimal !== undefined) {
        const decimalResult = this.tileValidator.validateDecimalPlaces(config.Decimal);
        errors.push(...decimalResult.errors);
      }

      // Multiplier
      if (config.Multiplier !== undefined) {
        const multiplierResult = this.tileValidator.validateMultiplier(config.Multiplier.toString());
        errors.push(...multiplierResult.errors);
      }

      // Offset
      if (config.Offset !== undefined) {
        const offsetResult = this.tileValidator.validateOffset(config.Offset);
        errors.push(...offsetResult.errors);
      }

      // Conversion formula
      if (config.Conversion !== undefined && config.Conversion !== '') {
        const conversionResult = this.tileValidator.validateConversionFormula(config.Conversion);
        errors.push(...conversionResult.errors);
      }

      // Min/Max writable values
      if (config.MinAllowed !== null && config.MinAllowed !== undefined &&
          config.MaxAllowed !== null && config.MaxAllowed !== undefined) {
        const minMaxResult = this.tileValidator.validateMinMax(config.MinAllowed, config.MaxAllowed);
        errors.push(...minMaxResult.errors);
      }
    }

    return errors;
  }

  /**
   * Validate bool (toggle) tile specific fields
   */
  private validateBoolTile(tile: Tile): string[] {
    const errors: string[] = [];
    const structure = tile.Structure as SimpleBoolStructure;

    // Validate alarm conditions
    structure.Conditions.forEach((condition) => {
      // Validate delay for all conditions
      const delayResult = this.tileValidator.validateAlarmDelay(condition.Delay);
      errors.push(...delayResult.errors);

      if (condition.Enabled) {
        // Validate alarm value is present and valid
        const valueResult = this.tileValidator.validateAlarmValue(
          'Critical',
          condition.Comparison.Value,
          condition.Enabled
        );
        errors.push(...valueResult.errors);
      }
    });

    return errors;
  }

  /**
   * Main entry point for mapping CSV data to a Tile
   * Uses pre-constructed Point ID from protocol handler
   */
  mapTile(row: CSVTileRow, deviceId: DeviceId, pointId: string): Tile | null {
    const tileType = this.detectTileType(row);

    if (tileType === TileFormat.Toggle) {
      return this.mapToggleTile(row as CSVToggleTileRow, deviceId, pointId);
    } else if (tileType === TileFormat.Numeric) {
      return this.mapNumericTile(row as CSVNumericTileRow, deviceId, pointId);
    }

    // Unknown tile type
    return null;
  }

  /**
   * Map toggle tile - supports BACnet, Modbus, and future protocols
   */
  mapToggleTile(row: CSVToggleTileRow, deviceId: DeviceId, pointId: string): Tile {
    const tile = this.createBaseTile(row, TILE_TYPE_SIMPLE_BOOL);

    // Determine protocol from data or parameter
    const detectedProtocol = this.detectProtocol(row);

    // Parse writable - default to false if empty, throw if invalid non-empty value
    const writableValue = row[CsvFieldId.TILE_WRITABILITY];
    const writable = CSVFieldParser.parseBoolean(writableValue);
    if (writable === null && writableValue && writableValue.trim() !== '') {
      throw new Error(`Cannot parse Tile Writability: "${writableValue}". Must be "true" or "false" (case-insensitive)`);
    }

    tile.Structure = {
      DeviceID: deviceId,
      Writable: writable ?? false,
      PointID: pointId,
      Protocol: detectedProtocol!,
      Priority: TILE_DEFAULT_WRITE_PRIORITY,
      AutoClear: CSVFieldParser.parseAutoClear(row[CsvFieldId.AUTO_CLEAR_ALARMS]),
      DisplayTrue: row[CsvFieldId.DATA_DISPLAY_TRUE_PHRASE],
      DisplayFalse: row[CsvFieldId.DATA_DISPLAY_FALSE_PHRASE],
      Conditions: this.createToggleConditions(row)
    } as SimpleBoolStructure;

    tile.Appearance = this.createAppearance(row);
    tile.setDefaults();
    return tile;
  }

  /**
   * Map numeric tile - supports BACnet, Modbus, and future protocols
   */
  mapNumericTile(row: CSVNumericTileRow, deviceId: DeviceId, pointAddress: string, protocol?: string): Tile {
    const tile = this.createBaseTile(row, TILE_TYPE_SIMPLE_ANALOG);

    // Determine protocol from data or parameter
    const detectedProtocol = protocol ?? this.detectProtocol(row);
    const conditions = this.createNumericConditions(row);

    // Parse writable - default to false if empty, throw if invalid non-empty value
    const writableValue = row[CsvFieldId.TILE_WRITABILITY];
    const writable = CSVFieldParser.parseBoolean(writableValue);
    if (writable === null && writableValue && writableValue.trim() !== '') {
      throw new Error(`Cannot parse Tile Writability: "${writableValue}". Must be "true" or "false" (case-insensitive)`);
    }

    tile.Structure = {
      DeviceID: deviceId,
      Writable: writable ?? false,
      PointID: pointAddress,
      Protocol: detectedProtocol!,
      Priority: TILE_DEFAULT_WRITE_PRIORITY,
      AutoClear: CSVFieldParser.parseAutoClear(row[CsvFieldId.AUTO_CLEAR_ALARMS]),
      DisplayRawUnits: row[CsvFieldId.DATA_DISPLAY_UNITS],
      Conditions: conditions
    } as SimpleAnalogStructure;

    // Parse min/max values - throw if field provided but can't parse
    const minStr = row[CsvFieldId.MINIMUM_WRITABLE_VALUE];
    const maxStr = row[CsvFieldId.MAXIMUM_WRITABLE_VALUE];
    let minAllowed: number | undefined;
    let maxAllowed: number | undefined;

    if (minStr) {
      minAllowed = CSVFieldParser.parseNumber(minStr);
      if (minAllowed === null) {
        throw new Error(`Cannot parse Minimum Writable Value: "${minStr}". Must be a valid number`);
      }
    }

    if (maxStr) {
      maxAllowed = CSVFieldParser.parseNumber(maxStr);
      if (maxAllowed === null) {
        throw new Error(`Cannot parse Maximum Writable Value: "${maxStr}". Must be a valid number`);
      }
    }

    // Build Config object with default values (matching makeTileFromBacnetPoint)
    const config: any = {
      Decimal: TILE_APPEARANCE_DEFAULT_DECIMAL,
      Increment: TILE_APPEARANCE_DEFAULT_INCREMENT,
      Multiplier: TILE_APPEARANCE_DEFAULT_MULTIPLIER,
    };

    // Override with CSV values if provided
    const decimal = CSVFieldParser.parseInt(row[CsvFieldId.DECIMAL_PLACES]);
    if (decimal !== null) {
      config.Decimal = decimal as TileSetPointDecimal;
    }

    const multiplier = CSVFieldParser.parseNumber(row[CsvFieldId.MULTIPLIER]);
    if (multiplier !== null) {
      config.Multiplier = multiplier as TileSetPointMultiplier;
    }

    const offset = CSVFieldParser.parseNumber(row[CsvFieldId.CALIBRATION_OFFSET]);
    if (offset !== null) {
      config.Offset = offset;
    }

    const conversion = row[CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA];
    if (conversion !== undefined) {
      config.Conversion = this.normalizeConversionFormula(conversion) as TileSetPointConversion;
    }

    if (minAllowed !== undefined) {
      config.MinAllowed = minAllowed;
    }

    if (maxAllowed !== undefined) {
      config.MaxAllowed = maxAllowed;
    }

    tile.Appearance = {
      ...this.createAppearance(row),
      Config: config
    };

    tile.setDefaults();
    return tile;
  }

  /**
   * Parse linked IDs
   */
  parseLinkedAppIds(value?: string): AppId[] {
    return CSVFieldParser.parseIdList(value);
  }

  // === Formula tile processing ===

  private buildAndValidateFormulaTile(row: CSVTileRow): { tile: Tile | null, formulaConfig?: FormulaConfig, errors: string[] } {
    const errors: string[] = [];
    const rawRow = row as any;

    // Validate formula identity
    const formulaTypeRaw = rawRow[CsvFieldId.FORMULA_TYPE] as string;
    const formulaValue = (rawRow[CsvFieldId.FORMULA_VALUE] as string)?.trim();

    if (formulaTypeRaw !== FormulaType.Template && formulaTypeRaw !== FormulaType.Custom) {
      errors.push(`Formula Type must be "${FormulaType.Template}" or "${FormulaType.Custom}"`);
      return { tile: null, errors };
    }
    const formulaType = formulaTypeRaw as FormulaType;

    if (!formulaValue) {
      errors.push('Formula Value is required');
      return { tile: null, errors };
    }

    if (formulaType === FormulaType.Template && !FORMULA_TEMPLATES.find(t => t.id === formulaValue)) {
      errors.push(`Unknown formula template: "${formulaValue}"`);
      return { tile: null, errors };
    }

    // Parse bindings
    const { bindings, errors: bindingErrors } = this.parseFormulaBindings(rawRow);
    if (bindingErrors.length > 0) {
      return { tile: null, errors: bindingErrors };
    }
    if (bindings.length === 0) {
      errors.push('At least one binding is required');
      return { tile: null, errors };
    }
    if (bindings.length > FORMULA_MAX_BINDINGS) {
      errors.push(`Maximum ${FORMULA_MAX_BINDINGS} bindings are allowed, but ${bindings.length} were provided`);
      return { tile: null, errors };
    }

    // For template formulas: validate binding variable names match the template's declared variables
    if (formulaType === FormulaType.Template) {
      const template = FORMULA_TEMPLATES.find(t => t.id === formulaValue)!;
      const expectedVars = template.variables.map(v => v.key);
      const actualVars = bindings.map(b => b.Variable);
      const missingVars = expectedVars.filter(v => !actualVars.includes(v));
      const extraVars = actualVars.filter(v => !expectedVars.includes(v));
      if (missingVars.length > 0) {
        errors.push(`Missing required binding variable(s) for template "${formulaValue}": ${missingVars.join(', ')}`);
      }
      if (extraVars.length > 0) {
        errors.push(`Unknown binding variable(s) for template "${formulaValue}": ${extraVars.join(', ')}. Expected: ${expectedVars.join(', ')}`);
      }
      if (errors.length > 0) {
        return { tile: null, errors };
      }
    }

    // For custom formulas: verify every variable referenced in the expression has a binding row,
    // and every defined binding is actually used in the expression.
    // Expression syntax is validated by the backend; this only checks structural completeness.
    if (formulaType === FormulaType.Custom) {
      const referencedVars = extractFormulaVariables(formulaValue);
      const boundVars = new Set(bindings.map(b => b.Variable));

      const missingBindings = referencedVars.filter(v => !boundVars.has(v));
      if (missingBindings.length > 0) {
        errors.push(`Custom formula references variable(s) with no binding: ${missingBindings.join(', ')}`);
        return { tile: null, errors };
      }

      const referencedVarSet = new Set(referencedVars);
      const orphanVars = bindings.map(b => b.Variable).filter(v => !referencedVarSet.has(v));
      if (orphanVars.length > 0) {
        errors.push(`Binding variable(s) not referenced in formula: ${orphanVars.join(', ')}`);
        return { tile: null, errors };
      }
    }

    // Build and validate tile
    let tile: Tile;
    try {
      tile = this.mapNumericTile(row as CSVNumericTileRow, '', '', PROTOCOL_FORMULA);
    } catch (error: any) {
      return { tile: null, errors: [`Mapping error: ${error.message}`] };
    }

    const validationErrors = this.validateTile(tile);
    if (validationErrors.length > 0) {
      return { tile: null, errors: validationErrors };
    }

    const formulaConfig: FormulaConfig = {
      Points: [{
        ID: '',
        Formula: { Type: formulaType, Value: formulaValue },
        Bindings: bindings
      }]
    };

    return { tile, formulaConfig, errors: [] };
  }

  private parseFormulaBindings(row: any): { bindings: FormulaBinding[], errors: string[] } {
    const bindings: FormulaBinding[] = [];
    const errors: string[] = [];
    const seenVariables = new Set<string>();
    const validDataSources = Object.values(LoRaWANPointType) as string[];
    const validDataTypes = Object.values(LoRaWANPointFormat) as string[];
    const VARIABLE_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/;

    for (let n = 1; ; n++) {
      const variable = (row[`Binding ${n}: Variable`] as string)?.trim();
      if (!variable) break;

      if (!VARIABLE_NAME_RE.test(variable)) {
        errors.push(`Binding ${n}: variable name "${variable}" must start with a letter and contain only letters, digits, or underscores`);
        continue;
      }

      if (seenVariables.has(variable)) {
        errors.push(`Binding ${n}: duplicate variable name "${variable}" — each binding must have a unique variable name`);
        continue;
      }
      seenVariables.add(variable);

      const deviceId = (row[`Binding ${n}: ${CsvFieldId.MOTION_DEVICE_ID}`] as string)?.trim();
      if (!deviceId) {
        errors.push(`Binding ${n}: Motion Device ID is required`);
        continue;
      }

      const pointName = (row[`Binding ${n}: ${CsvFieldId.LORAWAN_POINT_NAME}`] as string)?.trim();
      const dataSource = (row[`Binding ${n}: ${CsvFieldId.LORAWAN_DATA_SOURCE}`] as string)?.trim();
      const dataType = (row[`Binding ${n}: ${CsvFieldId.LORAWAN_DATA_TYPE}`] as string)?.trim();

      if (!pointName || !dataSource || !dataType) {
        errors.push(`Binding ${n}: LoRaWAN Point Name, Data Source, and Data Type are required`);
        continue;
      }

      if (!validDataSources.includes(dataSource)) {
        errors.push(`Binding ${n}: Data Source "${dataSource}" is invalid. Valid values: ${validDataSources.join(', ')}`);
        continue;
      }

      if (!validDataTypes.includes(dataType)) {
        errors.push(`Binding ${n}: Data Type "${dataType}" is invalid. Valid values: ${validDataTypes.join(', ')}`);
        continue;
      }

      const pointId = makeLoRaWANPointID(
        dataSource as LoRaWANPointType,
        pointName,
        dataType as LoRaWANPointFormat
      );

      const source: FormulaBindingSource = { DeviceID: deviceId, PointID: pointId };

      const multiplier = CSVFieldParser.parseNumber(row[`Binding ${n}: ${CsvFieldId.MULTIPLIER}`]);
      const conversionRaw = this.normalizeConversionFormula((row[`Binding ${n}: ${CsvFieldId.TRANSFORMATION_CONVERSION_FORMULA}`] as string)?.trim());
      const offset = CSVFieldParser.parseNumber(row[`Binding ${n}: ${CsvFieldId.CALIBRATION_OFFSET}`]);

      if (conversionRaw) {
        const validConversions: string[] = Object.values(ConversionFormula).filter(v => v !== ConversionFormula.None);
        if (!validConversions.includes(conversionRaw)) {
          errors.push(`Binding ${n}: invalid Transformation Conversion Formula "${conversionRaw}". Valid values: ${validConversions.join(', ')}`);
          continue;
        }
      }

      const hasConversion = conversionRaw !== undefined;
      const conversion = conversionRaw ?? ConversionFormula.None;

      if (multiplier !== null || hasConversion || offset !== null) {
        source.Transformations = {};
        if (multiplier !== null) source.Transformations.Multiplier = multiplier;
        if (hasConversion) source.Transformations.Conversion = conversion;
        if (offset !== null) source.Transformations.Offset = offset;
      }

      bindings.push({ Variable: variable, Source: source });
    }

    return { bindings, errors };
  }

  parseLinkedSuperTileIds(value?: string): TileId[] {
    return CSVFieldParser.parseIdList(value);
  }

  private normalizeConversionFormula(value: string | undefined): string | undefined {
    if (value === undefined) return undefined;
    return value?.toLowerCase() === 'none' ? ConversionFormula.None : value;
  }

  private validateTagsAndFacets(row: CSVTileRow): string[] {
    const errors: string[] = [];
    const tagLabels = this.parseTagLabels((row as any)[CsvFieldId.TAGS]);
    const facetPairs = this.parseFacetPairs((row as any)[CsvFieldId.FACETS]);

    const profileLabel = ((row as any)[CsvFieldId.PROFILE] ?? '').trim();
    if (profileLabel) {
      const validLabels = Object.values(TAG_PROFILES).map(p => p.Label);
      const isValid = validLabels.some(l => l.toLowerCase() === profileLabel.toLowerCase());
      if (!isValid) {
        errors.push(`Profile "${profileLabel}" is not recognized. Valid profiles: ${validLabels.join(', ')}`);
      }
    }

    for (const label of tagLabels) {
      if (label.includes('|')) {
        errors.push(`Tag label "${label}" contains '|' which is not allowed`);
      }
    }

    for (const {key, value} of facetPairs) {
      if (!key) {
        errors.push(`Facets column has an empty key (check for leading/trailing/double pipes)`);
      } else if (key.includes('|')) {
        errors.push(`Facet key "${key}" contains '|' which is not allowed`);
      }
      if (!value) {
        errors.push(`Facet "${key}" is missing a value (expected KEY=Value format)`);
      }
    }

    return errors;
  }

  // === Private Helpers ===

  private createBaseTile(row: CSVTileRow, type: TileType): Tile {
    // Match normal tile creation - use new Tile() directly, don't set ID or Enabled
    // Backend will assign these when the tile is created
    const tile = new Tile();
    tile.Type = type;
    tile.Name = row[CsvFieldId.TILE_NAME];
    return tile;
  }

  private createAppearance(row: CSVTileRow): Appearance {
    // Only set fields that are provided - tile.setDefaults() will fill in the rest
    return {
      Icon: row[CsvFieldId.ICON] as TileIcon,
      Color: row[CsvFieldId.COLOR] as ColorOption,
      DisplayName: row[CsvFieldId.DISPLAY_NAME],
    } as Appearance;
  }

  private createToggleConditions(row: CSVToggleTileRow): Condition[] {
    // Start with default conditions as single source of truth
    const conditions = Tile.getDefaultBoolConditions();

    const isEnabled = CSVFieldParser.parseBoolean(row[CsvFieldId.CRITICAL_ENABLED]);

    if (!isEnabled) {
      return conditions; // Keep default for disabled alarm
    }

    const parsedValue = CSVFieldParser.parseBoolean(row[CsvFieldId.CRITICAL_VALUE]);
    const value = parsedValue === true ? 1 : parsedValue === false ? 0 : null;

    conditions[0] = {
      Enabled: true,
      Mode: ConditionMode.Critical,
      Comparison: { Operator: CompareOperator.Equal, Value: value as number },
      DisplayText: row[CsvFieldId.CRITICAL_MESSAGE] || '',
      Icon: (row[CsvFieldId.CRITICAL_ICON] || '') as TileIcon,
      Delay: CSVFieldParser.parseInt(row[CsvFieldId.CRITICAL_DELAY]) || 0
    };

    return conditions;
  }

  private createNumericConditions(row: CSVNumericTileRow): Condition[] {
    // Start with default conditions as single source of truth
    const conditions = Tile.getDefaultAnalogConditions();

    return conditions.map((condition, index) => {
      const prefix = ALARM_LEVEL_NAMES[index];
      const isEnabled = CSVFieldParser.parseBoolean(row[`${prefix}: Enabled` as keyof CSVNumericTileRow]);

      if (!isEnabled) {
        return condition; // Keep default for disabled alarms
      }

      const operator = row[`${prefix}: Operator` as keyof CSVNumericTileRow];

      return {
        Enabled: true,
        Mode: condition.Mode,
        Comparison: {
          Operator: (operator || condition.Comparison.Operator) as AnalogAlarmOperator,
          Value: CSVFieldParser.parseNumber(row[`${prefix}: Value` as keyof CSVNumericTileRow])
        },
        DisplayText: row[`${prefix}: Message` as keyof CSVNumericTileRow] || '',
        Icon: (row[`${prefix}: Icon` as keyof CSVNumericTileRow] || '') as TileIcon | '',
        Delay: CSVFieldParser.parseInt(row[`${prefix}: Delay` as keyof CSVNumericTileRow]) || 0
      };
    });
  }
}
